/**
 * The upgrade prompts' UI (see promptConfig.ts for who sees what and why).
 * Each renders nothing unless its placement is switched on for the viewer's
 * billing state, so callers mount them unconditionally.
 */
import React, { useEffect, useState } from 'react';
import { useUpgradeGate } from '../useUpgradeGate';
import {
  bannerDismissed, dismissBanner, fillUpgradeText, promptFeature, trackUpgradePrompt, trialEnded, useUpgradePrompts,
} from './promptConfig';

const Spark: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3l2.2 5.3L20 9.2l-4.4 3.8L17 19l-5-3-5 3 1.4-6L4 9.2l5.8-.9L12 3z"
      stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
  </svg>
);

const Lock: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const Clock: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Opens the plans sheet for a prompt, recorded as that prompt. `ctx` rides
 *  on the paywall_shown event (which control, which surface). */
const useOpenPlans = () => {
  const upgrade = useUpgradeGate();
  return (placement: Parameters<typeof promptFeature>[0], ctx?: Record<string, unknown>) =>
    upgrade.gate(false, promptFeature(placement), undefined, undefined, ctx);
};

/** "Upgrade to Voyager" on Cosmos search (the owner's mock): `pane` fills the
 *  top of the history column on the web, `toolbar` sits at the right of the
 *  Filters / History row on a phone, `inline` sits beside the search box (the
 *  Explorer landing, onboarding v2). `surface` tags the click for the funnel
 *  ('cosmos_picks' on the Explorer landing). */
export const UpgradeSearchButton: React.FC<{ variant: 'pane' | 'toolbar' | 'inline'; surface?: string }> = ({ variant, surface }) => {
  const { config, viewer, shows } = useUpgradePrompts();
  const open = useOpenPlans();
  const on = shows('search_button');
  useEffect(() => {
    if (on) trackUpgradePrompt('shown', 'search_button', viewer.audience);
  }, [on, viewer.audience]);
  if (!on) return null;
  const sub = viewer.audience === 'trial_active' && viewer.daysLeft !== null ? `Trial · ${viewer.daysLeft}d left` : '';
  return (
    <button type="button"
      onClick={() => {
        trackUpgradePrompt('click', 'search_button', viewer.audience, { layout: variant, ...(surface ? { surface } : {}) });
        open('search_button', { control: 'search_button', layout: variant, ...(surface ? { surface } : {}) });
      }}
      title={sub || undefined}
      className={variant === 'pane'
        ? 'w-full h-10 rounded-lg bg-primary-600 text-grayscale-100 text-sm font-semibold hover:bg-primary-700 shadow-sm'
        : variant === 'inline'
          ? 'h-[50px] px-4 rounded-xl bg-primary-600 text-grayscale-100 text-sm font-semibold hover:bg-primary-700 whitespace-nowrap shadow-sm'
          : 'h-8 px-3 rounded-lg bg-primary-600 text-grayscale-100 text-xs font-semibold hover:bg-primary-700 whitespace-nowrap'}>
      {config.search_button.label}
      {variant === 'pane' && sub && <span className="block text-[10px] font-normal opacity-90 -mt-0.5">{sub}</span>}
    </button>
  );
};

/** Whether UpgradeSearchButton renders for this viewer. */
export const useSearchButtonOn = () => useUpgradePrompts().shows('search_button');

/** Side rail (48 px column): a gold tile with its label under it, the same
 *  rhythm as the nav items above it. `compact` is the mobile-header pill. */
export const UpgradeNavButton: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { config, viewer, shows } = useUpgradePrompts();
  const open = useOpenPlans();
  const on = shows('nav_button');
  useEffect(() => {
    if (on) trackUpgradePrompt('shown', 'nav_button', viewer.audience);
  }, [on, viewer.audience]);
  if (!on) return null;
  const label = config.nav_button.label;
  const sub = viewer.audience === 'trial_active' && viewer.daysLeft !== null
    ? `${viewer.daysLeft}d left` : '';
  const click = () => {
    trackUpgradePrompt('click', 'nav_button', viewer.audience, { layout: compact ? 'mobile_header' : 'rail' });
    open('nav_button');
  };
  if (compact) {
    return (
      <button type="button" onClick={click}
        className="flex items-center gap-1 h-8 px-3 rounded-full bg-primary-600 text-grayscale-100 text-xs font-semibold shadow-sm hover:bg-primary-700">
        <Spark className="w-3.5 h-3.5" />{label}
      </button>
    );
  }
  return (
    <button type="button" onClick={click} title={sub ? `${label} · trial ${sub}` : `${label} to Voyager`}
      className="flex w-12 flex-col items-center gap-[6px] group">
      <span className="flex w-12 h-12 items-center justify-center rounded-[9px] bg-primary-600 text-grayscale-100 shadow-md group-hover:bg-primary-700 transition-colors">
        <Spark />
      </span>
      <span className="w-14 text-center font-grotesk text-[9px] leading-tight font-semibold text-primary-700">
        {label}{sub && <span className="block font-normal text-grayscale-1100">{sub}</span>}
      </span>
    </button>
  );
};

/** A strip across the top of Cosmos search. Dismissed for the tab session. */
export const UpgradeBanner: React.FC = () => {
  const { config, viewer, shows } = useUpgradePrompts();
  const open = useOpenPlans();
  const [hidden, setHidden] = useState(bannerDismissed);
  const on = shows('banner') && !hidden;
  useEffect(() => {
    if (on) trackUpgradePrompt('shown', 'banner', viewer.audience);
  }, [on, viewer.audience]);
  if (!on || !viewer.audience) return null;
  const b = config.banner;
  const message = fillUpgradeText(
    viewer.audience === 'trial_active' ? b.message_trial_active
      : viewer.audience === 'trial_lapsed' ? b.message_trial_lapsed : b.message_explorer,
    viewer);
  return (
    <div role="region" aria-label="Upgrade"
      className="flex items-center gap-3 rounded-xl border border-primary-300 bg-gradient-to-r from-primary-100 to-white px-3 py-2.5 mb-3">
      <span className="hidden sm:flex w-8 h-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary-600 text-grayscale-100">
        <Spark className="w-4 h-4" />
      </span>
      <p className="flex-1 min-w-0 text-[13px] leading-snug text-grayscale-1300">{message}</p>
      <button type="button"
        onClick={() => { trackUpgradePrompt('click', 'banner', viewer.audience); open('banner'); }}
        className="flex-shrink-0 h-8 px-3.5 rounded-lg bg-primary-600 text-grayscale-100 text-xs font-semibold hover:bg-primary-700 whitespace-nowrap">
        {b.cta}
      </button>
      <button type="button" aria-label="Dismiss"
        onClick={() => { trackUpgradePrompt('dismiss', 'banner', viewer.audience); dismissBanner(); setHidden(true); }}
        className="flex-shrink-0 w-7 h-7 rounded-full text-grayscale-1100 hover:bg-grayscale-200 text-lg leading-none">×</button>
    </div>
  );
};

/** Top of the filter panel while filters are locked. Returns null when the
 *  placement is off, so the caller keeps its plain one-line lock notice. */
export const FilterUpgradeCard: React.FC = () => {
  const { config, viewer, shows } = useUpgradePrompts();
  const upgrade = useUpgradeGate();
  const on = shows('filter_card');
  useEffect(() => {
    if (on) trackUpgradePrompt('shown', 'filter_card', viewer.audience);
  }, [on, viewer.audience]);
  if (!on) return null;
  const f = config.filter_card;
  return (
    <div className="rounded-xl border border-primary-300 bg-primary-100/50 p-3 mb-3">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 text-primary-700"><Lock /></span>
        <div className="text-[13px] font-semibold text-grayscale-1300 leading-snug">{fillUpgradeText(f.headline, viewer)}</div>
      </div>
      <div className="text-[11px] text-grayscale-1100 mt-1 ml-6">Included with Voyager</div>
      <button type="button"
        // The same lock the old one-line notice raised, so the funnel's
        // 'full filtering' breakdown keeps counting these; the placement is
        // on the upgrade_prompt_click event.
        onClick={() => {
          trackUpgradePrompt('click', 'filter_card', viewer.audience);
          upgrade.gate(false, 'full filtering', undefined, 'can_filter', { control: 'filter', filter: 'filter_card' });
        }}
        className="mt-2.5 w-full h-8 rounded-lg bg-primary-600 text-grayscale-100 text-xs font-semibold hover:bg-primary-700">
        {f.cta}
      </button>
    </div>
  );
};

/** Whether FilterUpgradeCard renders — the caller hides its old lock line. */
export const useFilterCardOn = () => useUpgradePrompts().shows('filter_card');

/** Top of the filter panel during a Voyager trial: "Upgrade to Voyager" and
 *  the time left, ticking once a minute. Renders nothing unless the
 *  placement is on and the viewer is on an active trial. Filters are open
 *  during a trial, so the click opens the plans sheet as this prompt rather
 *  than raising the 'full filtering' lock. */
export const TrialUpgradeCard: React.FC = () => {
  const { config, viewer, shows } = useUpgradePrompts();
  const open = useOpenPlans();
  const on = shows('trial_card') && viewer.audience === 'trial_active';
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return undefined;
    trackUpgradePrompt('shown', 'trial_card', viewer.audience);
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => window.clearInterval(t);
  }, [on, viewer.audience]);
  if (!on) return null;
  const c = config.trial_card;
  // Past the end time, until the billing summary reloads and the state
  // becomes trial_lapsed (the filter card then takes over).
  const ended = trialEnded(viewer.endsAt, now);
  return (
    <div className="rounded-xl border border-primary-300 bg-primary-100/50 p-3 mb-3">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 text-primary-700"><Clock /></span>
        <div className="text-[13px] font-semibold text-grayscale-1300 leading-snug">{fillUpgradeText(c.headline, viewer, now)}</div>
      </div>
      <div className="text-[11px] text-grayscale-1100 mt-1 ml-6 leading-snug">
        {ended ? 'Your trial has ended.' : fillUpgradeText(c.message, viewer, now)}
      </div>
      <button type="button"
        onClick={() => {
          trackUpgradePrompt('click', 'trial_card', viewer.audience, { days_left: viewer.daysLeft });
          open('trial_card');
        }}
        className="mt-2.5 w-full h-8 rounded-lg bg-primary-600 text-grayscale-100 text-xs font-semibold hover:bg-primary-700">
        {c.cta}
      </button>
    </div>
  );
};
